/**
 * TRUE BAR-BY-BAR UNVEILING: the price series stops at the playhead, the axis does not.
 *
 * <h2>What this replaces, and why the old answer was not enough</h2>
 *
 * The replay used to draw every bar and move an amber line across them. The annotations were
 * bounded at the playhead, so no trade, exit or vector leaked — but the PRICE did, and price is
 * the thing an operator is being asked to form a view about. A reader deciding whether to buy at
 * bar 400 could see bar 401.
 *
 * <p>The veil that once covered the right-hand side was removed in 2026-10 because a full-height
 * band whose edge moved every tick flickered and made the price action hard to read. This is the
 * same guarantee done properly: the bars are not covered, they are NOT THERE.
 *
 * <h2>THE AXIS KEEPS ITS FULL SPAN, which is what makes the structure drawable</h2>
 *
 * <pre>
 *   x axis     |--- real bars ------------------|--- 250 synthetic weekdays ---|
 *   price      |--- revealed ---|---- null -----|----------- null -------------|
 *   pillars    |  PTA   PTB     |   PTA         |   PTB      CTS              |
 *                               ^ playhead
 * </pre>
 *
 * Nulls rather than a short array, deliberately. Both render the same thing today — a candlestick
 * series shorter than its category axis simply stops — but a null datum is a statement that the
 * column exists and has no price, and that is what lets the y-axis extent and every index-based
 * lookup behave as if the series spanned the chart. A short array also silently changes meaning
 * the moment anything reads `data.length` as "how many columns are there".
 *
 * <h2>AN EMPTY DATUM IS `[null, null, null, null]`, NEVER A BARE `null`</h2>
 *
 * This shipped wrong and crashed the chart the first time anyone pressed Launch:
 *
 * <pre>
 *   TypeError: Cannot read properties of null (reading 'value')
 *     at WhiskerBoxCommonMixin.getInitialData
 * </pre>
 *
 * ECharts' candlestick reader walks the data and branches on the datum's shape —
 * {@code isArray(item)} first, then {@code isArray(item.value)}. A bare `null` fails the first test
 * and dereferences on the second. There is no null check, so the series cannot take one.
 *
 * <p><b>The repository already had the right answer and it was not looked up.</b> `App`'s study
 * mode masks a bar with `[null, null, null, null]`, and `extendBarData` pads with real tuples —
 * both because a candlestick datum has a shape and an absent bar is a tuple of absent values, not
 * an absent tuple. The line view then reads `[1]` and gets `null`, which is what draws a gap, so
 * one representation serves all three chart types.
 *
 * <p><b>And each empty datum is its OWN array.</b> The reader above does
 * {@code item.unshift(index)} on the caller's arrays — it mutates what it is given — so a single
 * shared placeholder reused across 9,000 columns would accumulate indices and corrupt every one of
 * them. Fresh tuples cost one allocation per column per axis change, which happens on a symbol
 * load and never on a tick.
 *
 * <h2>Why no test caught it, which is the more useful lesson</h2>
 *
 * Every Phase 3 test drives a FAKE ECharts instance and asserts on the option object, because jsdom
 * cannot render a chart canvas. That is the right level for "is the data truncated at the
 * playhead" and it is structurally blind to "will ECharts accept this datum". The unit under test
 * was correct and the picture was broken — the same shape as every other defect in this file that
 * only a browser found. {@link EMPTY_BAR} and its test now pin the datum SHAPE against the
 * convention `App` already used, which is the closest a non-rendering test can get.
 *
 * <h2>O(1) PER TICK, by keeping one buffer and writing one slot</h2>
 *
 * Rebuilding a 9,000-element array on every tick is affordable but pointless: a step forward
 * reveals one bar. {@link revealTo} writes only the slots that changed and nulls only the slots a
 * step BACKWARD un-reveals, so the work is proportional to the move, not to the series.
 *
 * <p>What is NOT avoidable is ECharts re-reading the array — that cost belongs to the renderer and
 * is the same whoever builds the data. A shallow copy is handed over rather than the buffer itself,
 * because passing a reference ECharts has already seen risks it short-circuiting on identity, and
 * a copy of 9,000 references is microseconds.
 */

/**
 * A column the market has not traded — a FRESH tuple every time, never a shared one.
 *
 * <p>A function rather than a constant precisely because ECharts mutates the arrays it is handed
 * (`item.unshift(index)`), so one instance reused across the axis would accumulate indices.
 */
export const EMPTY_BAR = () => [null, null, null, null];

/** Whether a datum is a revealed bar rather than an empty column. */
export const isRevealed = (datum) =>
  Array.isArray(datum) && datum.some((v) => v !== null && v !== undefined);

/**
 * A buffer sized to the chart's full axis, with nothing revealed.
 *
 * @param length how many columns the axis has — the EXTENDED length, padding included
 * @param floor  the FIRST column this buffer may ever reveal. 0 for a run with no session, which
 *               is the research dialog's path and the behaviour every earlier phase had.
 *
 *   A SESSION SETS IT TO ITS OWN FIRST BAR, and that is what makes the erase mean anything: the
 *   theatre clears the chart to nothing to remove historical bias, and a buffer floored at 0
 *   would put 7,983 bars of history back on the very next frame. The operator accumulates their
 *   context from the session's own first bar forward, which is the point.
 */
export function createRevealBuffer(length, floor = 0) {
  const size = Number.isFinite(length) && length > 0 ? Math.trunc(length) : 0;
  const base = Number.isFinite(floor) ? Math.max(0, Math.min(Math.trunc(floor), size)) : 0;
  return {
    /** The first revealable column. Nothing below it is ever filled. */
    floor: base,
    /**
     * One entry per axis column, every unrevealed one an EMPTY TUPLE.
     *
     * <p>Not `null`: ECharts' candlestick reader dereferences `item.value` on anything that is not
     * an array, so a bare null throws before the chart draws. See the note at the top of this
     * file — it crashed the first launch.
     */
    data: Array.from({ length: size }, EMPTY_BAR),
    /**
     * Volumes, zero where unrevealed.
     *
     * <p>Zero and not null, because the volume series is a BAR chart: a null bar is absent, which
     * is right, but the pane's own scale is computed from the data and a mixture reads cleanly
     * either way. Zero is what the padding already uses in `extendBarData`, so the unrevealed tail
     * and the synthetic future look the same — which they are.
     */
    volumes: new Array(size).fill(0),
    /** The highest index currently revealed; `floor - 1` for nothing, which is -1 unfloored. */
    cursor: base - 1,
    length: size,
  };
}

/**
 * Reveal up to and including {@code upToIndex}, or hide back down to it.
 *
 * <p>Mutates the buffer and returns it. The work is O(|upToIndex - cursor|): one slot for an
 * ordinary step, the whole span for a jump, and nothing at all when the playhead has not moved.
 *
 * @param buffer  from {@link createRevealBuffer}
 * @param barData the chart's own `{ohlc, volumes}` — the REAL bars, not the padded series
 * @param upToIndex the playhead, in axis columns. -1 reveals nothing
 */
export function revealTo(buffer, barData, upToIndex) {
  if (!buffer) return buffer;
  const ohlc = Array.isArray(barData?.ohlc) ? barData.ohlc : [];
  const volumes = Array.isArray(barData?.volumes) ? barData.volumes : [];

  // Clamped to the buffer, never to the bar count: a playhead past the last REAL bar is a
  // legitimate position on a padded axis, and it should reveal every real bar and no more.
  //
  // AND NEVER BELOW THE FLOOR. `floor - 1` is the empty state for a floored buffer, exactly as
  // -1 is for an unfloored one, so every comparison below reads the same either way.
  const base = buffer.floor || 0;
  const target = Math.max(base - 1, Math.min(Math.trunc(upToIndex), buffer.length - 1));

  if (target > buffer.cursor) {
    for (let i = Math.max(buffer.cursor + 1, base); i <= target; i++) {
      // A column past the real bars stays EMPTY. That is the synthetic future: the axis has a
      // column and the market has no price for it, which is what a tuple of nulls says.
      buffer.data[i] = i < ohlc.length ? ohlc[i] : EMPTY_BAR();
      buffer.volumes[i] = i < volumes.length && Number.isFinite(volumes[i]) ? volumes[i] : 0;
    }
  } else if (target < buffer.cursor) {
    // STEPPING BACK HIDES AGAIN. Without this the transport's Arrow Left would leave the bars it
    // had already revealed on screen, so walking backwards would show the operator a future they
    // had just stepped away from — the asymmetry that makes a one-way reveal worse than none.
    for (let i = Math.max(target + 1, base); i <= buffer.cursor; i++) {
      buffer.data[i] = EMPTY_BAR();
      buffer.volumes[i] = 0;
    }
  }

  buffer.cursor = target;
  return buffer;
}

/**
 * The buffer's empty state — `floor - 1`, which is -1 when there is no floor.
 *
 * <p>Named rather than written out at each call site: "wind it all the way back" is a thing the
 * erase sequence does on every frame, and `-1` would quietly stop working the day a floored
 * buffer was handed to it.
 */
export const emptyCursor = (buffer) => (buffer ? (buffer.floor || 0) - 1 : -1);

/**
 * The pure form, for callers that have no buffer to keep — and for the tests.
 *
 * <p>Identical output to a buffer revealed to the same index, which
 * `replayReveal.test.js` asserts: two implementations of one truncation would be the usual defect,
 * so this one is defined as the specification and the buffer is checked against it.
 */
export function revealedSeries(barData, upToIndex, axisLength, floor = 0) {
  const buffer = createRevealBuffer(axisLength, floor);
  revealTo(buffer, barData, upToIndex);
  return { data: buffer.data, volumes: buffer.volumes };
}

/**
 * How many columns are revealed. Useful for a readout, and for asserting the truncation.
 */
export const revealedCount = (buffer) =>
  (buffer ? Math.max(0, buffer.cursor + 1 - (buffer.floor || 0)) : 0);
